import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  WarningIcon,
  InfoIcon,
  WarningCircleIcon,
  XCircleIcon,
  CheckCircleIcon,
  TrashIcon,
  DownloadIcon,
} from '@phosphor-icons/react';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DeltaAnalysisResult } from '@erp/shared-types';
import { Num, Plural, T, Var, useGT } from 'gt-react';

interface DeltaWarningDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  analysis: DeltaAnalysisResult | null;
  isLoading?: boolean;
  invoices?: Array<{
    id: string;
    files?: {
      transformedFile?: { id: string; name: string } | null;
    };
  }>;
  onDownloadDocument?: (documentId: string, fileName?: string) => void;
}

const SeverityIcon = ({ severity }: { severity: string }) => {
  switch (severity) {
    case 'destructive':
      return <XCircleIcon className="h-5 w-5 text-red-600" />;
    case 'major':
      return <WarningCircleIcon className="h-5 w-5 text-orange-600" />;
    case 'minor':
      return <InfoIcon className="h-5 w-5 text-blue-600" />;
    default:
      return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
  }
};

const SeverityBadge = ({ severity }: { severity: string }) => {
  const variants: Record<string, 'destructive' | 'default' | 'secondary'> = {
    destructive: 'destructive',
    major: 'default',
    minor: 'secondary',
    none: 'secondary',
  };

  return (
    <Badge variant={variants[severity]} className="ml-2">
      {severity.toUpperCase()}
    </Badge>
  );
};

export const DeltaWarningDialog: React.FC<DeltaWarningDialogProps> = ({
  open,
  onClose,
  onConfirm,
  analysis,
  isLoading,
  invoices,
  onDownloadDocument,
}) => {
  const gt = useGT();
  if (!analysis) return null;

  // Helper function to get transformed file for an invoice
  const getTransformedFile = (invoiceId: string) => {
    if (!invoices) return null;
    const invoice = invoices.find((inv) => inv.id === invoiceId);
    return invoice?.files?.transformedFile || null;
  };

  const {
    severity,
    activeProductGroupsDelta,
    invoiceImpact,
    paymentImpact,
    manualAmountImpact,
    exclusiveRuleWarning,
    reportedDataImpact,
  } = analysis;

  const hasRemovedGroups = activeProductGroupsDelta.removed.length > 0;
  const hasAddedGroups = activeProductGroupsDelta.added.length > 0;
  const hasExclusiveRuleWarning = exclusiveRuleWarning?.hasExclusiveRules;
  const hasRulesWithReportedData =
    (reportedDataImpact?.rulesWithReportedData?.length ?? 0) > 0;

  const severityDescription =
    severity === 'destructive'
      ? gt(
          'These changes will affect existing vendor reported amounts and payments. Please review the impacts carefully before proceeding.',
          { context: 'rebate agreement delta dialog destructive severity' },
        )
      : severity === 'major'
        ? gt(
            'These changes will impact existing payment allocations and rebate calculations. Review the details below.',
            { context: 'rebate agreement delta dialog major severity' },
          )
        : severity === 'minor'
          ? gt(
              'These changes have minimal impact. You can proceed with confidence.',
              { context: 'rebate agreement delta dialog minor severity' },
            )
          : gt(
              'These changes are safe and will not affect any existing data.',
              { context: 'rebate agreement delta dialog none severity' },
            );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="!max-w-[90vw] sm:!max-w-[90vw] !w-[90vw] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <SeverityIcon severity={severity} />
            <span className="ml-2">
              <T context="rebate agreement delta dialog title">
                Review Agreement Changes
              </T>
            </span>
            <SeverityBadge severity={severity} />
          </DialogTitle>
          <DialogDescription>{severityDescription}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {/* Product Groups Delta */}
          {(hasRemovedGroups || hasAddedGroups) && (
            <Card className="p-4">
              <h3 className="text-lg font-semibold mb-3">
                <T context="rebate delta section title">
                  Product Coverage Changes
                </T>
              </h3>
              <div className="space-y-2">
                {hasRemovedGroups && (
                  <AlertContainer variant="destructive">
                    <WarningIcon className="h-4 w-4" />
                    <AlertTitle>
                      <T context="rebate delta alert title">
                        Products Being Removed from Agreement
                      </T>
                    </AlertTitle>
                    <AlertDescription>
                      <div className="mt-2 text-sm mb-2">
                        <T context="rebate delta product groups removed">
                          The following product groups will no longer be covered
                          by this agreement:
                        </T>
                      </div>
                      <div className="mt-2 space-y-1">
                        {activeProductGroupsDelta.removed.map((group) => (
                          <div key={group} className="font-mono text-sm">
                            <T context="rebate delta product group line">
                              • <Var>{group}</Var>
                            </T>
                          </div>
                        ))}
                      </div>
                    </AlertDescription>
                  </AlertContainer>
                )}
                {hasAddedGroups && (
                  <AlertContainer>
                    <InfoIcon className="h-4 w-4" />
                    <AlertTitle>
                      <T context="rebate delta alert title">
                        Products Being Added to Agreement
                      </T>
                    </AlertTitle>
                    <AlertDescription>
                      <div className="mt-2 text-sm mb-2">
                        <T context="rebate delta product groups added">
                          The following product groups will now be covered by
                          this agreement:
                        </T>
                      </div>
                      <div className="mt-2 space-y-1">
                        {activeProductGroupsDelta.added.map((group) => (
                          <div key={group} className="font-mono text-sm">
                            <T context="rebate delta product group line">
                              • <Var>{group}</Var>
                            </T>
                          </div>
                        ))}
                      </div>
                    </AlertDescription>
                  </AlertContainer>
                )}
              </div>
            </Card>
          )}

          {/* Exclusive Rule Warning */}
          {hasExclusiveRuleWarning && (
            <Card className="p-4">
              <h3 className="text-lg font-semibold mb-3">
                <T context="rebate delta section title">
                  ⚠️ Exclusive Rules Impact
                </T>
              </h3>
              <AlertContainer
                variant={
                  invoiceImpact.totalInvoices > 0 ? 'destructive' : 'default'
                }
              >
                <WarningIcon className="h-4 w-4" />
                <AlertTitle>
                  <T context="rebate delta exclusive rules alert title">
                    Changes May Affect Exclusive Rule Calculations
                  </T>
                </AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm">
                    <T context="rebate delta exclusive rules description">
                      Adding new product groups may change which products are
                      included or excluded in your exclusive rules. This could
                      affect rebate calculations.
                    </T>
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="font-semibold text-sm">
                      <T context="rebate delta subsection heading">
                        Products Being Added:
                      </T>
                    </div>
                    <div className="space-y-1">
                      {exclusiveRuleWarning.addedProductGroups.map((group) => (
                        <div key={group} className="font-mono text-sm ml-4">
                          <T context="rebate delta product group line">
                            • <Var>{group}</Var>
                          </T>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="font-semibold text-sm">
                      <T context="rebate delta subsection heading">
                        Rules That May Be Affected:
                      </T>
                    </div>
                    {exclusiveRuleWarning.affectedRules.map((rule) => (
                      <div
                        key={rule.ruleName}
                        className="p-3 bg-orange-50 rounded text-sm border border-orange-200"
                      >
                        <div className="font-semibold">{rule.ruleName}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {rule.qualifyingIsExclusive && (
                            <T context="rebate exclusive rule flag">
                              Has exclusive qualifying products •{' '}
                            </T>
                          )}
                          {rule.affectedIsExclusive && (
                            <T context="rebate exclusive rule flag">
                              Has exclusive rebate products •{' '}
                            </T>
                          )}
                          <T context="rebate exclusive rule excluded groups">
                            Currently excluding:{' '}
                            <Var>
                              {rule.currentExcludedGroups.length > 0
                                ? rule.currentExcludedGroups.join(', ')
                                : gt('None', {
                                    context:
                                      'rebate exclusive excluded groups empty',
                                  })}
                            </Var>
                          </T>
                        </div>
                      </div>
                    ))}
                  </div>
                  {invoiceImpact.totalInvoices > 0 && (
                    <div className="mt-3 p-3 bg-red-50 rounded border border-red-200">
                      <div className="text-sm font-semibold text-red-900">
                        <T context="rebate delta historical data warning title">
                          ⚠️ Historical Data Warning
                        </T>
                      </div>
                      <div className="text-sm text-red-800 mt-1">
                        <T context="rebate delta historical data warning body">
                          <Num>{invoiceImpact.totalInvoices}</Num> existing
                          vendor reported{' '}
                          <Plural
                            n={invoiceImpact.totalInvoices}
                            one="amount"
                            other="amounts"
                          />{' '}
                          may calculate different rebate amounts after these
                          changes due to the expanded product scope.
                        </T>
                      </div>
                    </div>
                  )}
                </AlertDescription>
              </AlertContainer>
            </Card>
          )}

          {/* Rules with Reported Data - Will be set to Inactive instead of deleted */}
          {hasRulesWithReportedData && (
            <Card className="p-4">
              <h3 className="text-lg font-semibold mb-3">
                <T context="rebate delta section title">
                  Rules with Historical Data
                </T>
              </h3>
              <AlertContainer
                variant="default"
                className="border-yellow-300 bg-yellow-50"
              >
                <WarningIcon className="h-4 w-4 text-yellow-600" />
                <AlertTitle className="text-yellow-800">
                  <T context="rebate delta inactive rules alert title">
                    Rules Will Be Set to Inactive (Not Deleted)
                  </T>
                </AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm text-yellow-800">
                    <T context="rebate delta inactive rules description">
                      The following rules have historical reported data that
                      must be preserved. Instead of being deleted, they will be
                      marked as <strong>inactive</strong>. Inactive rules are
                      excluded from future rebate calculations but their
                      historical data remains intact.
                    </T>
                  </div>
                  <div className="mt-3 space-y-2">
                    {reportedDataImpact?.rulesWithReportedData.map((rule) => (
                      <div
                        key={rule.ruleId}
                        className="p-3 bg-yellow-100 rounded text-sm border border-yellow-300 flex items-start gap-2"
                      >
                        <InfoIcon className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="font-semibold text-yellow-900">
                            {rule.ruleName}
                          </div>
                          <div className="text-xs text-yellow-700 mt-1">
                            <T context="rebate delta inactive rule note">
                              Will be set to inactive to preserve reported
                              amounts
                            </T>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </AlertDescription>
              </AlertContainer>
            </Card>
          )}

          {/* Section 1: CSV/Invoice Impact */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-3">
              <T context="rebate delta section title">
                Impact on Existing Vendor Reported Amounts
              </T>
            </h3>
            {invoiceImpact.incompatibleInvoices > 0 ? (
              <AlertContainer variant="destructive">
                <XCircleIcon className="h-4 w-4" />
                <AlertTitle>
                  <T context="rebate delta incompatible invoices title">
                    <Num>{invoiceImpact.incompatibleInvoices}</Num> Vendor
                    Reported{' '}
                    <Plural
                      n={invoiceImpact.incompatibleInvoices}
                      one="Amount"
                      other="Amounts"
                    />{' '}
                    Will No Longer Process
                  </T>
                </AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm">
                    <T context="rebate delta incompatible invoices description">
                      These vendor reported amounts contain products that are
                      being removed from the agreement. They will not be able to
                      earn rebates after this change, and be deleted.
                    </T>
                  </div>
                  <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                    {invoiceImpact.incompatibleDetails.map((detail) => {
                      const transformedFile = getTransformedFile(
                        detail.invoiceId,
                      );
                      return (
                        <div
                          key={detail.invoiceId}
                          className="p-3 bg-red-50 rounded text-sm border border-red-200 flex items-start gap-2"
                        >
                          <TrashIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-semibold">
                              {gt('Vendor Reported Amount from {date}', {
                                date: new Date(
                                  detail.invoiceDate,
                                ).toLocaleDateString(),
                                context: 'rebate delta invoice row title',
                              })}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              <T context="rebate delta invoice row detail">
                                <Num>{detail.affectedLineItems}</Num>{' '}
                                <Plural
                                  n={detail.affectedLineItems}
                                  one="line item"
                                  other="line items"
                                />{' '}
                                will no longer qualify • Products being removed:{' '}
                                <Var>
                                  {detail.missingProductGroups.join(', ')}
                                </Var>
                              </T>
                            </div>
                          </div>
                          {transformedFile && onDownloadDocument && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 px-2 flex-shrink-0"
                              onClick={() =>
                                onDownloadDocument(
                                  transformedFile.id,
                                  transformedFile.name,
                                )
                              }
                            >
                              <DownloadIcon className="h-3 w-3 mr-1" />
                              <T context="download button">Download</T>
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </AlertDescription>
              </AlertContainer>
            ) : (
              <AlertContainer>
                <CheckCircleIcon className="h-4 w-4 text-green-600" />
                <AlertTitle>
                  <T context="rebate delta compatible invoices title">
                    All Vendor Reported Amounts Compatible
                  </T>
                </AlertTitle>
                <AlertDescription>
                  {invoiceImpact.totalInvoices === 0 ? (
                    <T context="rebate delta no invoices to check">
                      No existing vendor reported amounts to check.
                    </T>
                  ) : (
                    <T context="rebate delta all invoices compatible">
                      All <Num>{invoiceImpact.totalInvoices}</Num> existing
                      vendor reported{' '}
                      <Plural
                        n={invoiceImpact.totalInvoices}
                        one="amount"
                        other="amounts"
                      />{' '}
                      will continue to work with these changes.
                    </T>
                  )}
                </AlertDescription>
              </AlertContainer>
            )}
          </Card>

          {/* Section 2: Vendor Payment Allocations */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-3">
              <T context="rebate delta section title">
                Impact on Vendor Payment Allocations
              </T>
            </h3>
            {paymentImpact.affectedAllocations > 0 ? (
              <AlertContainer variant="destructive">
                <WarningCircleIcon className="h-4 w-4" />
                <AlertTitle>
                  <T context="rebate delta payment allocations title">
                    Payment Allocations Will Be Removed
                  </T>
                </AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm">
                    <T context="rebate delta payment allocations summary">
                      You are deleting{' '}
                      <Plural
                        n={paymentImpact.deletedRules.length}
                        one="rule"
                        other="rules"
                      />{' '}
                      <span className="font-semibold">
                        <Var>{paymentImpact.deletedRules.join(', ')}</Var>
                      </span>
                      . This will remove{' '}
                      <Num>{paymentImpact.affectedAllocations}</Num> payment{' '}
                      <Plural
                        n={paymentImpact.affectedAllocations}
                        one="allocation"
                        other="allocations"
                      />{' '}
                      totaling{' '}
                      <span className="font-semibold">
                        <Var>
                          ${paymentImpact.affectedAmount.toLocaleString()}
                        </Var>
                      </span>{' '}
                      that{' '}
                      <Plural
                        n={paymentImpact.affectedAllocations}
                        one="was"
                        other="were"
                      />{' '}
                      allocated to{' '}
                      <Plural
                        n={paymentImpact.deletedRules.length}
                        one="this rule"
                        other="these rules"
                      />
                      .
                    </T>
                  </div>
                  {paymentImpact.unallocatableDetails.length > 0 && (
                    <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                      {paymentImpact.unallocatableDetails.map((detail) => (
                        <div
                          key={detail.paymentId}
                          className="p-3 bg-red-50 rounded text-sm border border-red-200 flex items-start gap-2"
                        >
                          <TrashIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-semibold mb-1">
                              {gt('Payment: {amount} on {date}', {
                                amount: `$${detail.paymentAmount.toLocaleString()}`,
                                date: new Date(
                                  detail.paymentDate,
                                ).toLocaleDateString(),
                                context: 'rebate delta payment row title',
                              })}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {gt(
                                '{amount} allocated to {rules} will be removed',
                                {
                                  amount: `$${detail.allocationAmount.toLocaleString()}`,
                                  rules: detail.affectedRules.join(', '),
                                  context: 'rebate delta payment row detail',
                                },
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </AlertDescription>
              </AlertContainer>
            ) : (
              <AlertContainer>
                <CheckCircleIcon className="h-4 w-4 text-green-600" />
                <AlertTitle>
                  <T context="rebate delta no payment impact title">
                    No Payment Impact
                  </T>
                </AlertTitle>
                <AlertDescription>
                  {paymentImpact.totalPayments === 0 ? (
                    <T context="rebate delta no payments to check">
                      No vendor payments to check.
                    </T>
                  ) : (
                    <T context="rebate delta all payments intact">
                      All <Num>{paymentImpact.totalPayments}</Num> vendor{' '}
                      <Plural
                        n={paymentImpact.totalPayments}
                        one="payment"
                        other="payments"
                      />{' '}
                      will remain intact.
                    </T>
                  )}
                </AlertDescription>
              </AlertContainer>
            )}
          </Card>

          {/* Section 3: Manual Rebate Rule Payments */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-3">
              <T context="rebate delta section title">
                Impact on Manually Recorded Rebates
              </T>
            </h3>
            {manualAmountImpact.affectedManualAmounts > 0 ? (
              <AlertContainer variant="destructive">
                <WarningCircleIcon className="h-4 w-4" />
                <AlertTitle>
                  <T context="rebate delta manual rebates title">
                    Manually Recorded Rebates Will Be Removed
                  </T>
                </AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm">
                    <T context="rebate delta manual rebates summary">
                      You are deleting{' '}
                      <Plural
                        n={manualAmountImpact.deletedRules.length}
                        one="rule"
                        other="rules"
                      />{' '}
                      <span className="font-semibold">
                        <Var>{manualAmountImpact.deletedRules.join(', ')}</Var>
                      </span>
                      . This will remove{' '}
                      <Num>{manualAmountImpact.affectedManualAmounts}</Num>{' '}
                      manually recorded{' '}
                      <Plural
                        n={manualAmountImpact.affectedManualAmounts}
                        one="rebate"
                        other="rebates"
                      />{' '}
                      totaling{' '}
                      <span className="font-semibold">
                        <Var>
                          {`$${manualAmountImpact.affectedTotal.toLocaleString()}`}
                        </Var>
                      </span>{' '}
                      that{' '}
                      <Plural
                        n={manualAmountImpact.affectedManualAmounts}
                        one="was"
                        other="were"
                      />{' '}
                      tied to{' '}
                      <Plural
                        n={manualAmountImpact.deletedRules.length}
                        one="this rule"
                        other="these rules"
                      />
                      .
                    </T>
                  </div>
                  {manualAmountImpact.manualAmountDetails.length > 0 && (
                    <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                      {manualAmountImpact.manualAmountDetails.map((detail) => (
                        <div
                          key={detail.manualAmountId}
                          className="p-3 bg-red-50 rounded text-sm border border-red-200 flex items-start gap-2"
                        >
                          <TrashIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-semibold mb-1">
                              {gt(
                                'Manually Recorded Rebate: {amount} on {date}',
                                {
                                  amount: `$${detail.amount.toLocaleString()}`,
                                  date: new Date(
                                    detail.date,
                                  ).toLocaleDateString(),
                                  context:
                                    'rebate delta manual rebate row title',
                                },
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              <T context="rebate delta manual rebate row meta">
                                Division: <Var>{detail.divisionName}</Var> •
                                Rule: <Var>{detail.ruleName}</Var>
                              </T>
                              {detail.description ? (
                                <T context="rebate delta manual rebate row note">
                                  {' '}
                                  • Note: <Var>{detail.description}</Var>
                                </T>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </AlertDescription>
              </AlertContainer>
            ) : (
              <AlertContainer>
                <CheckCircleIcon className="h-4 w-4 text-green-600" />
                <AlertTitle>
                  <T context="rebate delta no manual rebate impact title">
                    No Manually Recorded Rebate Impact
                  </T>
                </AlertTitle>
                <AlertDescription>
                  {manualAmountImpact.totalManualAmounts === 0 ? (
                    <T context="rebate delta no manual rebates to check">
                      No manually recorded rebates to check.
                    </T>
                  ) : (
                    <T context="rebate delta all manual rebates intact">
                      All <Num>{manualAmountImpact.totalManualAmounts}</Num>{' '}
                      manually recorded{' '}
                      <Plural
                        n={manualAmountImpact.totalManualAmounts}
                        one="rebate"
                        other="rebates"
                      />{' '}
                      will remain intact.
                    </T>
                  )}
                </AlertDescription>
              </AlertContainer>
            )}
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            <T context="dialog back button">Go Back</T>
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
            variant={severity === 'destructive' ? 'destructive' : 'default'}
          >
            {isLoading ? (
              <T context="rebate delta save loading">Saving Changes...</T>
            ) : severity === 'destructive' ? (
              <T context="rebate delta destructive confirm">
                I Understand - Save Changes
              </T>
            ) : (
              <T context="rebate delta confirm save">Confirm & Save Changes</T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
